import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { CustomerProfile, Promotion } from '@/models';
import { inViewport } from '@/helpers/elements';
import { AnalyticsEventAction, AnalyticsEventCategory, AnalyticsEventLabel } from '@/services/google/google-analytics.services';
import { Routes } from '@/router';

@Component({
  name: 'CmgRewards',
})
export default class CmgRewards extends Vue {
  shouldShowFloatingTracker = true;

  async mounted(): Promise<void> {
    const isLoggedIn = await this.$services.customer.waitForCustomerInitialization();
    if (!isLoggedIn) {
      window.location.replace(`${window.location.origin}${this.$services.config.getRewardsPath()}`);
      return;
    }
    if (!this.$services.loyalty.isLoyaltyEligible(this.$appStore.state.customer.profile)) {
      this.$services.ui.setModalVisibility('rewardsCountryMismatch', true);
    }

    window.addEventListener('scroll', this.onScroll, true);
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardsLanding,
      action: AnalyticsEventAction.PageViewed,
      label: AnalyticsEventLabel.RewardsPageViewed,
    });
  }

  destroyed(): void {
    window.removeEventListener('scroll', this.onScroll, true);
  }

  onScroll(): void {
    if (this.isServiceOnline) {
      this.shouldShowFloatingTracker = !inViewport(this.$refs.rewardTracker as Element);
    }
  }

  get isLoggedIn(): boolean {
    return this.$services.customer.isLoggedIn() === true;
  }

  get loading(): boolean {
    this.$services.ui.nextFocus('point-rewards');
    const loading = !this.isCustomerInitialized || this.$appStore.state.ui.enrollingInRewards;
    this.$services.ui.setAppBlur(loading);
    this.$services.ui.setAppPepperVisibility(loading);
    return loading;
  }

  get isCustomerInitialized(): boolean {
    return this.$services.customer.isLoggedIn() !== undefined;
  }

  get isEnrolledInLoyalty(): boolean {
    return this.$services.loyalty.isEnrolledInLoyalty(this.$appStore.state.customer.profile);
  }

  get isLoyaltyEligible(): boolean {
    return this.$services.loyalty.isLoyaltyEligible(this.$appStore.state.customer.profile);
  }

  get isServiceOnline(): boolean {
    return this.isRewardsOnline && this.isRewardsExchangeOnline && !this.$appStore.state.reward.unreachable;
  }

  get isRewardsOnline(): boolean {
    return this.$appStore.state.featureFlags.orderingStatus?.isRewardsOnline ?? true;
  }

  get isRewardsExchangeOnline(): boolean {
    return this.$appStore.state.featureFlags.orderingStatus?.isRewardsExchangeOnline ?? true;
  }

  get promotions(): Promotion[] {
    return this.$appStore.state.customer.wallet.promotions;
  }

  get countryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get hasPromotions(): boolean {
    return this.$appStore.state.customer.wallet.promotions.length > 0;
  }

  get hasFavoriteReward(): boolean {
    return !!this.$services.reward.getFavoriteReward();
  }
  
  get isGuest(): boolean {
    if (!this.customer) {
      return true;
    }

    return this.customer.isGuest;
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }
}
